// eslint-disable-next-line import/named
import { createConfirmCancelButtons } from './modal-footer-buttons';
import { EventSnippet, Snippet } from '../../interop/webmodule-interop';
import { globalState } from './global-state';
import { whileBlockingDialogOpen } from './blocking-dialog-state';
import { html, TemplateResult } from 'lit';
import { LangModalValidationHandler } from '../../../localization/webmodule-common/other/ui/modal-validationhandler-lang';
import { LitBaseModal } from './modal/modal-factory-lit';

export interface CustomValidationButton {
  type: 'discard' | 'stay';
  label: string;
  action?: () => Promise<void>;
}
export async function showValidationsWithCustomOptions(
  items: string[],
  title: EventSnippet,
  buttons: CustomValidationButton[]
): Promise<string> {
  if (!title) title = () => LangModalValidationHandler.validationReport();
  const modal = new ValidationWithDiscardDialog(title, items, undefined, buttons);
  await modal.showModal();
  return modal.discard ? 'discard' : 'stay';
}
export async function showValidations(items: string[], title?: EventSnippet) {
  if (!title) title = () => LangModalValidationHandler.validationReport();
  await new ValidationWithDiscardDialog(title, items).showModal();
}

export async function showValidationsWithDiscard(
  items: string[],
  title?: EventSnippet,
  buttons?: ValidationButtons
): Promise<boolean> {
  if (!title) title = () => LangModalValidationHandler.validationReport();
  if (!buttons) buttons = getValidationButtons()[ValidationButtonType.discardStay];
  const modal = new ValidationWithDiscardDialog(title, items, buttons);
  await modal.showModal();
  return modal.discard;
}

export async function showValidationsWithAbandon(
  items: string[],
  title?: EventSnippet,
  buttons?: ValidationButtons
): Promise<boolean> {
  if (!title) title = () => LangModalValidationHandler.validationReport();
  if (!buttons) buttons = getValidationButtons()[ValidationButtonType.stayAbandon];
  const modal = new ValidationWithDiscardDialog(title, items, buttons);
  await modal.showModal();
  return modal.discard;
}

export interface ValidationButtons {
  discard: string;
  stay: string;
}
export interface ValidationButtonsProvider {
  discard: () => string;
  stay: () => string;
}

function getValidationButtons(): ValidationButtons[] {
  const gs = globalState();
  gs.validationButtons ??= [
    {
      discard: () => LangModalValidationHandler.discardAndClose(),
      stay: () => LangModalValidationHandler.stayOnPage()
    },
    {
      discard: () => LangModalValidationHandler.discardChanges(),
      stay: () => LangModalValidationHandler.stayOnPage()
    }
  ];
  return gs.validationButtons.map(buttons => ({
    discard: buttons.discard(),
    stay: buttons.stay()
  })) as ValidationButtons[];
}

export enum ValidationButtonType {
  discardStay = 0,
  stayAbandon = 1
}

export class ValidationWithDiscardDialog extends LitBaseModal {
  eventTitle: EventSnippet;
  issues: string[];
  buttons?: ValidationButtons;
  public discard: boolean = false;
  customButtons?: CustomValidationButton[];

  constructor(
    title: EventSnippet,
    issues: string[],
    buttons?: ValidationButtons,
    customButtons?: CustomValidationButton[]
  ) {
    super();
    this.eventTitle = title;
    this.issues = issues;
    this.buttons = buttons;

    this.customButtons = customButtons;
  }

  /**
   * Overridden ONLY to mark this dialog as blocking for its lifetime. Done here rather than in each
   * showValidations* helper so every variant is covered by construction and a new one cannot forget.
   *
   * While this is open, a "please wait" spinner that has not yet appeared stays hidden — otherwise it
   * lands on top of this dialog and the user cannot reach the message they have to dismiss to make the
   * spinner go away. See blocking-dialog-state.
   */
  public override async showModal(): Promise<void> {
    await whileBlockingDialogOpen(() => super.showModal());
  }

  protected get modalClasses(): string {
    return 'modal-dialog modal-dialog-scrollable';
  }

  protected get modalSize() {
    return 'modal-xl';
  }

  title(): Snippet {
    return this.eventTitle();
  }

  public bodyTemplate() {
    const warnings = this.issues.map(issue => html` <div class="alert alert-warning" role="alert">${issue}</div>`);
    return html`${warnings}`;
  }

  public footerTemplate(): TemplateResult | null {
    if (!this.buttons && !this.customButtons) {
      return html` <webmodule-button size="small" variant="default" @click=${() => this.hideModal()}
        >${LangModalValidationHandler.close()}
      </webmodule-button>`;
    }

    if (this.buttons) {
      const discardEvent = () => {
        this.discard = true;
        this.hideModal();
      };

      return createConfirmCancelButtons(this.buttons.discard, discardEvent, this.buttons.stay, () => this.hideModal());
    }
    if (this.customButtons) {
      const buttons = this.customButtons.map(
        button =>
          html` <webmodule-button
            size="small"
            variant="default"
            @click=${async () => {
              this.discard = button.type === 'discard';
              this.hideModal();
              await button.action?.();
            }}
            >${button.label}
          </webmodule-button>`
      );
      return html`${buttons}`;
    }
    return html``;
  }

  protected ZIndex(): number | undefined {
    return 99993;
  }
}
